import { CurrencyDisplayType } from '@components/Digit';
import {
  MassUnitTypeEnum,
  Maybe,
  TemperatureUnitTypeEnum,
  UnitOfCurrencyEnum,
  UnitOfCurrencyEnumV2,
  UnitOfLengthEnum,
  VolumeUnitTypeEnum,
} from '@generated/types';
import { DEBUG_STRING, hasLocalizationDebug } from '@utils/localize';
import { win } from '@utils/win';
import { isNil, isString } from 'lodash-es';
import { FC } from 'react';
import { MergeExclusive } from 'type-fest';
import {
  MassUnitTypeEnumV2,
  TemperatureUnitTypeEnumV2,
  UnitOfDensityEnum,
  UnitOfLengthEnumV2,
  UnitOfTimeEnum,
  VolumeUnitTypeEnumV2,
} from './enum';

// ts-unused-exports:disable-next-line
export type UnitType =
  | UnitOfLengthEnum
  | TemperatureUnitTypeEnum
  | UnitOfCurrencyEnum
  | MassUnitTypeEnum
  | VolumeUnitTypeEnum
  | UnitOfDensityEnum
  | CurrencyEnum
  | string;

const defaultLang = win.navigator.language;

export enum CurrencyEnum {
  Usd = 'USD',
  Mxn = 'MXN',
  Cad = 'CAD',
  Eur = 'EUR',
}

// https://tc39.es/proposal-unified-intl-numberformat/section6/locales-currencies-tz_proposed_out.html#sec-issanctionedsimpleunitidentifier
type AllowedECMAScriptUnit =
  | 'acre'
  | 'bit'
  | 'byte'
  | 'celsius'
  | 'centimeter'
  | 'day'
  | 'degree'
  | 'fahrenheit'
  | 'fluid-ounce'
  | 'foot'
  | 'gallon'
  | 'gigabit'
  | 'gigabyte'
  | 'gram'
  | 'hectare'
  | 'hour'
  | 'inch'
  | 'kilobit'
  | 'kilobyte'
  | 'kilogram'
  | 'kilometer'
  | 'liter'
  | 'megabit'
  | 'megabyte'
  | 'meter'
  | 'mile'
  | 'mile-scandinavian'
  | 'milliliter'
  | 'millimeter'
  | 'millisecond'
  | 'minute'
  | 'month'
  | 'ounce'
  | 'percent'
  | 'petabyte'
  | 'pound'
  | 'second'
  | 'stone'
  | 'terabit'
  | 'terabyte'
  | 'week'
  | 'yard'
  | 'year';

const formatIntlUnits = new Map<UnitType, AllowedECMAScriptUnit>([
  // length
  [UnitOfLengthEnumV2.Ft, 'foot'],
  [UnitOfLengthEnumV2.In, 'inch'],
  [UnitOfLengthEnumV2.M, 'meter'],
  [UnitOfLengthEnumV2.Mi, 'mile'],
  [UnitOfLengthEnumV2.Km, 'kilometer'],
  // temp
  [TemperatureUnitTypeEnumV2.F, 'fahrenheit'],
  [TemperatureUnitTypeEnumV2.C, 'celsius'],
  // mass
  [MassUnitTypeEnumV2.G, 'gram'],
  [MassUnitTypeEnumV2.Kg, 'kilogram'],
  [MassUnitTypeEnumV2.Lbs, 'pound'],
  [MassUnitTypeEnumV2.St, 'stone'],
]);

interface PropsWithFractionDigits {
  /** Sets min and max fraction digits to same. Use `minimumFractionDigits` and `maximumFractionDigits` to set a range. */
  fractionDigits?: number | undefined;
}

interface PropsWithMinOrMax {
  minimumFractionDigits?: number | undefined;
  maximumFractionDigits?: number | undefined;
}

interface BaseProps {
  value?: Maybe<number>;
  unit?: Maybe<UnitType>;
  language?: string;
  /** Fallback when there is no data present */
  noData?: string;
  zeroFallback?: string;
  suffix?: string;
  currencyDisplay?: CurrencyDisplayType;
  showUnit?: boolean;
}

export type Props = BaseProps &
  MergeExclusive<PropsWithFractionDigits, PropsWithMinOrMax>;

const suffixMap = new Map<
  | VolumeUnitTypeEnum
  | UnitOfDensityEnum
  | MassUnitTypeEnumV2
  | TemperatureUnitTypeEnumV2
  | UnitOfLengthEnumV2
  | VolumeUnitTypeEnumV2
  | UnitOfTimeEnum,
  string
>([
  [VolumeUnitTypeEnumV2.Ft3, 'ft³'],
  [VolumeUnitTypeEnumV2.In3, 'in³'],
  [VolumeUnitTypeEnumV2.Cm3, 'cm³'],
  [VolumeUnitTypeEnumV2.M3, 'm³'],
  [UnitOfDensityEnum.Lbft3, 'lb/ft³'],
  [UnitOfDensityEnum.Cwtft3, 'cwt/ft³'],
  [UnitOfDensityEnum.Lbin3, 'lb/in³'],
  [UnitOfDensityEnum.Kgcm3, 'kg/cm³'],
  [UnitOfDensityEnum.Kgm3, 'kg/m³'],
  [MassUnitTypeEnumV2.Lb, MassUnitTypeEnumV2.Lb],
  [MassUnitTypeEnumV2.Cwt, MassUnitTypeEnumV2.Cwt],
  [MassUnitTypeEnumV2.Oz, MassUnitTypeEnumV2.Oz],
  [MassUnitTypeEnumV2.T, 'T'],
  [MassUnitTypeEnumV2.Mt, MassUnitTypeEnumV2.Mt],
  [MassUnitTypeEnumV2.G, MassUnitTypeEnumV2.G],
  [MassUnitTypeEnumV2.Kg, MassUnitTypeEnumV2.Kg],
  [MassUnitTypeEnumV2.St, MassUnitTypeEnumV2.St],
  [UnitOfLengthEnumV2.Cm, UnitOfLengthEnumV2.Cm],
  [UnitOfLengthEnumV2.M, UnitOfLengthEnumV2.M],
  [TemperatureUnitTypeEnumV2.C, TemperatureUnitTypeEnumV2.C],
  [UnitOfTimeEnum.Hr, UnitOfTimeEnum.Hr],
  [UnitOfTimeEnum.Hours, UnitOfTimeEnum.Hours],
  [UnitOfTimeEnum.Mn, UnitOfTimeEnum.Mn],
  [UnitOfTimeEnum.Minutes, UnitOfTimeEnum.Minutes],
  [UnitOfTimeEnum.Days, UnitOfTimeEnum.Days],
]);

const genericFormatterMap = new Map<string, Intl.NumberFormat>();

const getFormatterId = (props: Props): string => {
  const lang = props.language ?? defaultLang;
  return `${lang}-${Object.values(props).join('-')}`;
};

const getFormatter = (
  props: Props,
  fallback: Parameters<typeof Intl.NumberFormat>['1'],
): Intl.NumberFormat => {
  const id = getFormatterId(props);
  let formatter = genericFormatterMap.get(id);

  if (!formatter) {
    const lang = props.language || defaultLang;
    formatter = new Intl.NumberFormat(lang, fallback);
    genericFormatterMap.set(id, formatter);
  }
  return formatter;
};

const isCurrency = (unit: Props['unit']): boolean =>
  Object.values(
    CurrencyEnum || UnitOfCurrencyEnum || UnitOfCurrencyEnumV2,
  ).includes(
    (unit as CurrencyEnum) ||
      (unit as UnitOfCurrencyEnum) ||
      (unit as UnitOfCurrencyEnumV2),
  );

const getFractionDigits = (
  props: Props,
): {
  maximumFractionDigits: number | undefined;
  minimumFractionDigits: number | undefined;
} => {
  const { fractionDigits, maximumFractionDigits, minimumFractionDigits, unit } =
    props;
  if (!isNil(fractionDigits)) {
    return {
      maximumFractionDigits: fractionDigits,
      minimumFractionDigits: fractionDigits,
    };
  } else if (!isNil(maximumFractionDigits) || !isNil(minimumFractionDigits)) {
    return {
      maximumFractionDigits: maximumFractionDigits,
      minimumFractionDigits: minimumFractionDigits,
    };
  } else if (isCurrency(unit) && unit) {
    return {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    };
  }
  return {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  };
};

const genericFormat = (props: Props): string => {
  const { value, unit, showUnit: sUnit } = props;
  if (isNil(value)) {
    return '';
  }
  const showUnit = sUnit === false ? false : true;

  const formatter = getFormatter(props, {
    ...getFractionDigits(props),
    style: 'unit',
    unit: 'foot',
  });

  return formatter
    .format(value)
    .replace('ft', showUnit ? suffixMap.get(unit as fixMe) || '' : '')
    .trim();
};

const shouldUseDebug = hasLocalizationDebug();

export const getNumberValue = (props: Maybe<Props>): string => {
  if (shouldUseDebug) {
    return DEBUG_STRING;
  }
  if (!props) {
    return '';
  }
  const { value, unit, suffix } = props;
  if (isNil(value)) {
    return props.noData || '';
  }

  if (value === 0 && isString(props.zeroFallback)) {
    return props.zeroFallback;
  }

  const isCurrency = Object.values(
    CurrencyEnum || UnitOfCurrencyEnum || UnitOfCurrencyEnumV2,
  ).includes(
    (unit as CurrencyEnum) ||
      (unit as UnitOfCurrencyEnum) ||
      (unit as UnitOfCurrencyEnumV2),
  );

  const foundMatchingIntlUnit = formatIntlUnits.get(unit as fixMe);

  let intermediateVal = '';

  const common = getFractionDigits(props);

  if (isCurrency && unit) {
    intermediateVal = getFormatter(props, {
      ...common,
      style: 'currency',
      currency: unit,
      currencyDisplay: props.currencyDisplay ?? 'symbol',
    }).format(value);
  } else if (foundMatchingIntlUnit && props.showUnit !== false) {
    intermediateVal = getFormatter(props, {
      ...common,
      style: 'unit',
      unit: foundMatchingIntlUnit,
    }).format(value);
  } else {
    intermediateVal = genericFormat(props);
  }
  return `${intermediateVal}${suffix ?? ''}`;
};

export const getUnitDisplay = (unit: Maybe<Props['unit']>): string => {
  if (!unit) {
    return '';
  }
  const str = getNumberValue({ value: 1, unit });
  return str.replace(/1/, '');
};

export const NumberValue: FC<Props> = (props) => {
  return <span {...props}>{getNumberValue(props)}</span>;
};
